// =============================================================================
// MeuDriver — Instructors Page (orchestrator)
// -----------------------------------------------------------------------------
// Filtros, comparação e listagem ficam em `./instructors/`. Esta página
// orquestra estado de URL + render principal.
//
// Filtros no topo (2026-10-11): Estado → Cidade (seletores; municípios do
// IBGE), "Procurar nas proximidades" (raio de NEARBY_KM do centro da cidade) e
// nome. O resto (especialidade, preço, nota…) fica em "Mais filtros".
// `embedded`: a mesma página dentro do painel do aluno (/aluno/instrutores),
// com o menu lateral — sem o cabeçalho de vitrine.
// =============================================================================

import { useState, useCallback, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Search,
  SlidersHorizontal,
  X,
  Clock,
  CalendarDays,
  GitCompare,
  ArrowRight,
  LayoutGrid,
  Map,
  MapPin,
  Loader2,
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

import { useInstructors } from '@/hooks/useInstructors'
import { useCidades, useCentroDaCidade, UFS, NEARBY_KM } from '@/hooks/useLocalidades'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useSEO } from '@/hooks/useSEO'
import { SPECIALTY_LABELS } from '@/lib/constants'
import { InstructorCard } from '@/components/instructor/InstructorCard'
import { InstructorCardSkeleton } from '@/components/instructor/InstructorCardSkeleton'
import { InstructorMapView } from '@/components/instructor/InstructorMapView'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { track } from '@/lib/analytics'
import { useAuth } from '@/hooks/useAuth'

import { FilterPanel, type FilterPanelProps } from './instructors/InstructorFilters'
import { CompareModal } from './instructors/InstructorCompareModal'

type SortOption = 'rating_desc' | 'price_asc' | 'price_desc' | 'experience_desc'

const ALL = '__all__'

/** Página de cidade para SEO: /instrutores-de-direcao/:uf/:cidade (2026-10-12). */
export interface CityLanding { uf: string; citySlug: string }

function slugifyCity(s: string) {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
}

export default function InstructorsPage({ embedded = false, landing }: { embedded?: boolean; landing?: CityLanding }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false)
  const [sort, setSort] = useState<SortOption>('rating_desc')
  const [compareIds, setCompareIds] = useState<Set<string>>(new Set())
  const [compareOpen, setCompareOpen] = useState(false)
  const [viewMode, setViewMode] = useState<'grid' | 'map'>('grid')
  // U3 — filtro de tags (client-side)
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])

  const search               = searchParams.get('search') ?? ''
  const uf                   = searchParams.get('uf') ?? ''
  const city                 = searchParams.get('city') ?? ''
  const nearby               = searchParams.get('near') === '1'
  const specialties          = searchParams.get('specialty')?.split(',').filter(Boolean) ?? []
  const minPrice             = searchParams.get('minPrice') ?? ''
  const maxPrice             = searchParams.get('maxPrice') ?? ''
  const minRating            = Number(searchParams.get('minRating') ?? 0)
  const availableToday       = searchParams.get('availableToday') === '1'
  const availableThisWeek    = searchParams.get('availableThisWeek') === '1'
  const availableDate        = searchParams.get('availableDate') ?? ''
  const womenOnly            = searchParams.get('womenOnly') === '1'
  // P1 — filtro de experiência mínima
  const minExperienceYears   = Number(searchParams.get('minExp') ?? 0)

  const { data: cidades = [], isLoading: cidadesLoading } = useCidades(uf || landing?.uf.toUpperCase() || '')
  const landingCity = landing ? cidades.find((c) => slugifyCity(c) === landing.citySlug) ?? null : null
  const landingUf = landing?.uf.toUpperCase() ?? ''

  useSEO({
    title: landingCity
      ? `Instrutores de direção em ${landingCity} - ${landingUf}`
      : 'Encontre Instrutores de Direção',
    description: landingCity
      ? `Aulas de direção com instrutores particulares em ${landingCity} (${landingUf}). Compare avaliações e preços e agende online no MeuDriver.`
      : 'Pesquise e compare instrutores de direção particulares na sua cidade. Avaliações reais, preços transparentes e agendamento online.',
    noIndex: embedded,
  })
  const { data: centro, isFetching: centroLoading } = useCentroDaCidade(city, uf, nearby)

  const updateParams = useCallback(
    (changes: Record<string, string>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        for (const [k, v] of Object.entries(changes)) {
          if (v) next.set(k, v)
          else next.delete(k)
        }
        return next
      }, { replace: true })
    },
    [setSearchParams],
  )
  const updateParam = useCallback((key: string, value: string) => updateParams({ [key]: value }), [updateParams])

  // Página de cidade: a busca começa naquela cidade.
  const landingApplied = useRef(false)
  useEffect(() => {
    if (!landing || landingApplied.current || !landingCity) return
    landingApplied.current = true
    updateParams({ uf: landingUf, city: landingCity })
  }, [landing, landingCity, landingUf, updateParams])

  // Dentro do painel do aluno, a busca já começa na cidade dele (perfil).
  const { profile } = useAuth()
  const prefilled = useRef(false)
  useEffect(() => {
    if (!embedded || prefilled.current || searchParams.toString()) return
    prefilled.current = true
    if (profile?.state && UFS.some((u) => u.uf === profile.state)) {
      updateParams({ uf: profile.state, city: profile.city ?? '' })
    }
  }, [embedded, profile?.state, profile?.city, searchParams, updateParams])

  // Busca por nome com debounce (a URL não muda a cada tecla).
  const [searchDraft, setSearchDraft] = useState(search)
  useEffect(() => { setSearchDraft(search) }, [search])
  useEffect(() => {
    if (searchDraft === search) return
    const t = setTimeout(() => updateParam('search', searchDraft.trim()), 350)
    return () => clearTimeout(t)
  }, [searchDraft, search, updateParam])

  const handleSpecialtyToggle = useCallback(
    (key: string) => {
      const next = specialties.includes(key)
        ? specialties.filter((s) => s !== key)
        : [...specialties, key]
      updateParam('specialty', next.join(','))
    },
    [specialties, updateParam],
  )

  const handleTagToggle = useCallback((tagId: string) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((t) => t !== tagId) : [...prev, tagId],
    )
  }, [])

  const handleClearFilters = () => {
    setSearchParams(new URLSearchParams())
    setSelectedTagIds([])
    setSearchDraft('')
  }

  const { instructors: allInstructors, isLoading } = useInstructors({
    search,
    state: uf || undefined,
    city: city || undefined,
    near: nearby && centro ? { lat: centro.lat, lng: centro.lng, km: NEARBY_KM } : undefined,
    specialties,
    minPrice: minPrice ? Number(minPrice) : undefined,
    maxPrice: maxPrice ? Number(maxPrice) : undefined,
    minRating: minRating || undefined,
    sort,
    availableToday: availableToday || undefined,
    availableThisWeek: availableThisWeek || undefined,
    availableDate: availableDate || undefined,
    womenOnly: womenOnly || undefined,
    minExperienceYears: minExperienceYears || undefined,
  })

  // U3 — Busca IDs de instrutores que possuem as tags selecionadas (filtragem server-side)
  const { data: tagFilteredIds } = useQuery<string[] | null>({
    queryKey: ['instructor-tag-filter', selectedTagIds],
    enabled: selectedTagIds.length > 0,
    queryFn: async () => {
      // Para cada tag, busca os instructor_ids; intersecção garante que tem TODAS as tags
      const results = await Promise.all(
        selectedTagIds.map((tagId) =>
          supabase
            .from('instructor_tags')
            .select('instructor_id')
            .eq('tag_id', tagId)
            .then(({ data }) => new Set((data ?? []).map((r) => r.instructor_id as string))),
        ),
      )
      const [first, ...rest] = results
      if (!first) return null
      const intersection = rest.reduce((acc, set) => {
        return new Set([...acc].filter((id) => set.has(id)))
      }, first)
      return Array.from(intersection)
    },
    staleTime: 1000 * 60,
  })

  const instructors =
    selectedTagIds.length > 0 && tagFilteredIds
      ? allInstructors.filter((inst) => tagFilteredIds.includes(inst.id))
      : allInstructors

  // Contagem do "Mais filtros" (nome/estado/cidade ficam visíveis no topo).
  const moreFilterCount = [
    ...specialties, minPrice, maxPrice,
    minRating > 0 ? '1' : '',
    availableDate,
    womenOnly ? '1' : '',
    minExperienceYears > 0 ? '1' : '',
    ...selectedTagIds,
  ].filter(Boolean).length
  const activeFilterCount = moreFilterCount + [search, uf, city, availableToday ? '1' : '', availableThisWeek ? '1' : ''].filter(Boolean).length

  // Posthog: `search_performed` quando os filtros param de mudar por 500 ms.
  const trackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (activeFilterCount === 0) return
    if (trackTimerRef.current) clearTimeout(trackTimerRef.current)
    trackTimerRef.current = setTimeout(() => {
      track('search_performed', {
        has_city: Boolean(city),
        specialty_count: specialties.length,
        has_price_range: Boolean(minPrice || maxPrice),
        min_rating: minRating || undefined,
        available_today: availableToday || undefined,
        available_this_week: availableThisWeek || undefined,
        has_available_date: Boolean(availableDate),
      })
    }, 500)
    return () => {
      if (trackTimerRef.current) clearTimeout(trackTimerRef.current)
    }
  }, [activeFilterCount, city, specialties.length, minPrice, maxPrice, minRating, availableToday, availableThisWeek, availableDate])

  const handleCompareToggle = (id: string) => {
    setCompareIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else if (next.size < 3) next.add(id)
      return next
    })
  }

  const compareList = instructors.filter((i) => compareIds.has(i.id))

  const filterPanelProps: FilterPanelProps = {
    specialties, minPrice, maxPrice, minRating, availableDate, womenOnly,
    minExperienceYears,
    selectedTagIds,
    onTagToggle: handleTagToggle,
    onSpecialtyToggle: handleSpecialtyToggle,
    onMinPriceChange: (v) => updateParam('minPrice', v),
    onMaxPriceChange: (v) => updateParam('maxPrice', v),
    onMinRatingChange: (v) => updateParam('minRating', v > 0 ? String(v) : ''),
    onAvailableDateChange: (v) => updateParam('availableDate', v),
    onWomenOnlyChange: (v) => updateParam('womenOnly', v ? '1' : ''),
    onMinExperienceYearsChange: (v) => updateParam('minExp', v > 0 ? String(v) : ''),
    onClear: handleClearFilters,
  }

  // ── Barra de filtros (topo) ───────────────────────────────────────────────
  const filterBar = (
    <div className="space-y-3">
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-[160px_minmax(0,1fr)_minmax(0,1fr)_auto]">
        <Select
          value={uf || ALL}
          onValueChange={(v) => updateParams({ uf: v === ALL ? '' : v, city: '', near: '' })}
        >
          <SelectTrigger className="h-10 bg-white" aria-label="Estado">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todos os estados</SelectItem>
            {UFS.map((u) => (
              <SelectItem key={u.uf} value={u.uf}>{u.nome}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={city || ALL}
          onValueChange={(v) => updateParams({ city: v === ALL ? '' : v, near: v === ALL ? '' : searchParams.get('near') ?? '' })}
          disabled={!uf || cidadesLoading}
        >
          <SelectTrigger className="h-10 bg-white" aria-label="Cidade">
            {cidadesLoading ? (
              <span className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" />Carregando cidades…</span>
            ) : (
              <SelectValue placeholder={uf ? 'Cidade' : 'Escolha o estado primeiro'} />
            )}
          </SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value={ALL}>Todas as cidades</SelectItem>
            {cidades.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Nome do instrutor..."
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            className="pl-9 h-10 bg-white"
          />
          {searchDraft && (
            <button type="button" onClick={() => setSearchDraft('')} aria-label="Limpar nome"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <Sheet open={moreFiltersOpen} onOpenChange={setMoreFiltersOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" className="h-10 gap-2 bg-white">
              <SlidersHorizontal className="h-4 w-4" />
              Mais filtros
              {moreFilterCount > 0 && (
                <Badge className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs bg-brand-600">
                  {moreFilterCount}
                </Badge>
              )}
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-80 overflow-y-auto">
            <SheetHeader className="mb-6">
              <SheetTitle>Mais filtros</SheetTitle>
            </SheetHeader>
            <FilterPanel {...filterPanelProps} />
          </SheetContent>
        </Sheet>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {city && !uf && (
          <Badge variant="secondary" className="gap-1.5 cursor-pointer hover:bg-gray-200 h-8" onClick={() => updateParams({ city: '', near: '' })}>
            Cidade: {city}<X className="h-3 w-3" />
          </Badge>
        )}
        <label
          className={cn(
            'inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border transition-all',
            city ? 'bg-white border-gray-200 text-gray-700 cursor-pointer' : 'bg-gray-50 border-gray-100 text-gray-400 cursor-not-allowed',
          )}
          title={city ? `Inclui instrutores a até ${NEARBY_KM} km de ${city}` : 'Escolha uma cidade para procurar nas proximidades'}
        >
          <MapPin className="h-3.5 w-3.5" />
          Procurar nas proximidades
          <Switch
            checked={nearby}
            disabled={!city}
            onCheckedChange={(v) => updateParam('near', v ? '1' : '')}
            className="scale-75"
          />
          {nearby && centroLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
        </label>
        <button
          onClick={() => updateParams({ availableToday: availableToday ? '' : '1', availableThisWeek: '' })}
          className={cn(
            'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all',
            availableToday
              ? 'bg-brand-600 text-white border-brand-600'
              : 'bg-white text-gray-600 border-gray-200 hover:border-brand-300 hover:text-brand-600',
          )}
        >
          <Clock className="h-3.5 w-3.5" />
          Disponível hoje
        </button>
        <button
          onClick={() => updateParams({ availableThisWeek: availableThisWeek ? '' : '1', availableToday: '' })}
          className={cn(
            'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all',
            availableThisWeek
              ? 'bg-brand-600 text-white border-brand-600'
              : 'bg-white text-gray-600 border-gray-200 hover:border-brand-300 hover:text-brand-600',
          )}
        >
          <CalendarDays className="h-3.5 w-3.5" />
          Disponível esta semana
        </button>
        {activeFilterCount > 0 && (
          <button type="button" onClick={handleClearFilters} className="text-sm text-brand-600 hover:underline ml-1">
            Limpar filtros
          </button>
        )}
      </div>
      {nearby && city && !centroLoading && centro === null && (
        <p className="text-xs text-amber-700">Não achamos a localização de {city}; mostrando só a própria cidade.</p>
      )}
    </div>
  )

  return (
    <div className={embedded ? 'space-y-6' : 'min-h-screen bg-gray-50'}>
      {embedded ? (
        <div className="space-y-4">
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">Buscar instrutores</h1>
            <p className="text-muted-foreground text-sm mt-1">Escolha o estado e a cidade para ver quem atende você.</p>
          </div>
          {filterBar}
        </div>
      ) : (
        <div className="bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-5">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {landingCity ? `Instrutores de direção em ${landingCity}` : 'Encontre seu instrutor'}
              </h1>
              <p className="mt-2 text-gray-500">
                {landingCity
                  ? `Aulas práticas com instrutores particulares em ${landingCity} - ${landingUf}. Compare avaliações, preços e horários e agende online.`
                  : 'Instrutores qualificados em todo o Brasil, prontos para te ajudar.'}
              </p>
            </div>
            {filterBar}
          </div>
        </div>
      )}

      <div className={embedded ? '' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'}>
        <div className="flex flex-wrap items-center gap-3 mb-6 overflow-hidden">
          <p className="text-sm text-gray-500">
            {isLoading ? (
              <span className="inline-block h-4 w-28 bg-gray-200 rounded animate-pulse" />
            ) : (
              <>
                <span className="font-semibold text-gray-900">{instructors.length}</span>{' '}
                {instructors.length === 1 ? 'instrutor encontrado' : 'instrutores encontrados'}
                {nearby && city && centro && ` em ${city} e até ${NEARBY_KM} km`}
              </>
            )}
          </p>
          {compareIds.size > 0 && (
            <span className="text-xs text-muted-foreground">
              {compareIds.size} selecionado{compareIds.size > 1 ? 's' : ''} para comparar
            </span>
          )}

          <div className="flex items-center gap-2 w-full sm:w-auto sm:ml-auto">
            <div className="flex items-center rounded-lg border border-gray-200 bg-white p-0.5 gap-0.5">
              <button
                onClick={() => setViewMode('grid')}
                className={cn(
                  'flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all',
                  viewMode === 'grid' ? 'bg-brand-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700',
                )}
                title="Visualização em grade"
              >
                <LayoutGrid className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Grade</span>
              </button>
              <button
                onClick={() => setViewMode('map')}
                className={cn(
                  'flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all',
                  viewMode === 'map' ? 'bg-brand-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700',
                )}
                title="Visualização no mapa"
              >
                <Map className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Mapa</span>
              </button>
            </div>

            <Select value={sort} onValueChange={(v) => setSort(v as SortOption)}>
              <SelectTrigger className="flex-1 sm:flex-none sm:w-44 h-9 text-sm bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="rating_desc">Mais bem avaliados</SelectItem>
                <SelectItem value="price_asc">Menor preço</SelectItem>
                <SelectItem value="price_desc">Maior preço</SelectItem>
                <SelectItem value="experience_desc">Mais experientes</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {moreFilterCount > 0 && (
          <div className="flex flex-wrap gap-2 mb-5">
            {specialties.map((s) => (
              <Badge key={s} variant="secondary" className="gap-1.5 cursor-pointer hover:bg-gray-200" onClick={() => handleSpecialtyToggle(s)}>
                {SPECIALTY_LABELS[s as keyof typeof SPECIALTY_LABELS] ?? s}<X className="h-3 w-3" />
              </Badge>
            ))}
            {(minPrice || maxPrice) && (
              <Badge variant="secondary" className="gap-1.5 cursor-pointer hover:bg-gray-200"
                onClick={() => updateParams({ minPrice: '', maxPrice: '' })}>
                Preço: R${minPrice || '0'} – R${maxPrice || '∞'}<X className="h-3 w-3" />
              </Badge>
            )}
            {minRating > 0 && (
              <Badge variant="secondary" className="gap-1.5 cursor-pointer hover:bg-gray-200" onClick={() => updateParam('minRating', '')}>
                {minRating}+ ★<X className="h-3 w-3" />
              </Badge>
            )}
          </div>
        )}

        {viewMode === 'map' && <InstructorMapView instructors={instructors} />}

        {viewMode === 'grid' && (
          isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {Array.from({ length: 6 }).map((_, i) => <InstructorCardSkeleton key={i} />)}
            </div>
          ) : instructors.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {instructors.map((instructor) => (
                <InstructorCard
                  key={instructor.id}
                  instructor={instructor}
                  isComparing={compareIds.has(instructor.id)}
                  onCompareToggle={() => handleCompareToggle(instructor.id)}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="rounded-full bg-gray-100 p-6 mb-4">
                <Search className="h-10 w-10 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Nenhum instrutor encontrado</h3>
              <p className="text-gray-500 max-w-sm">
                {city && !nearby
                  ? `Ainda não há instrutores em ${city}. Ligue "Procurar nas proximidades" para ver cidades vizinhas.`
                  : 'Tente ajustar os filtros ou ampliar a sua busca para encontrar instrutores disponíveis na sua região.'}
              </p>
              {city && !nearby ? (
                <Button type="button" className="mt-6" onClick={() => updateParam('near', '1')}>
                  <MapPin className="h-4 w-4 mr-1.5" />
                  Procurar nas proximidades
                </Button>
              ) : (
                <Button type="button" variant="outline" className="mt-6" onClick={handleClearFilters}>
                  Limpar filtros
                </Button>
              )}
            </div>
          )
        )}
      </div>

      <AnimatePresence>
        {compareIds.size >= 2 && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 bg-slate-900 text-white px-5 py-3 rounded-2xl shadow-2xl border border-white/10"
          >
            <div className="flex items-center gap-2">
              <GitCompare className="h-4 w-4 text-brand-400" />
              <span className="text-sm font-medium">
                {compareIds.size} instrutor{compareIds.size > 1 ? 'es' : ''} selecionado{compareIds.size > 1 ? 's' : ''}
              </span>
              {compareIds.size < 3 && (
                <span className="text-xs text-white/40">(selecione até {3 - compareIds.size} mais)</span>
              )}
            </div>
            <Button size="sm" className="bg-brand-600 hover:bg-brand-500 text-white h-8 text-xs px-4" onClick={() => setCompareOpen(true)}>
              Comparar
              <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
            <button onClick={() => setCompareIds(new Set())} className="text-white/40 hover:text-white/80 transition-colors">
              <X className="h-4 w-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <CompareModal instructors={compareList} open={compareOpen} onClose={() => setCompareOpen(false)} />
    </div>
  )
}
